import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { ArrowLeft, Download, Mail, Copy, FilePenLine, Trash2, Eraser, CheckCircle2, Pen } from 'lucide-react';
import { toast } from 'sonner';
import { getCsrfToken } from '../../lib/api';

const API = ''; // use relative URLs for production-safe API calls

const STATUS_CONFIG = {
  draft:    { label: 'Entwurf',       className: 'bg-gray-100 text-gray-700 border-gray-200' },
  sent:     { label: 'Gesendet',      className: 'bg-blue-100 text-blue-700 border-blue-200' },
  signed:   { label: 'Unterschrieben', className: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  completed:{ label: 'Abgeschlossen', className: 'bg-purple-100 text-purple-700 border-purple-200' },
  cancelled:{ label: 'Storniert',     className: 'bg-rose-100 text-rose-700 border-rose-200' },
};

function SignaturePad({ onSave, onClose, side }) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastRef = useRef({ x: 0, y: 0 });
  const hasDrawnRef = useRef(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#1a1a1a';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, []);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const t = e.touches?.[0];
    const cx = t ? t.clientX : e.clientX;
    const cy = t ? t.clientY : e.clientY;
    return { x: (cx - rect.left) * scaleX, y: (cy - rect.top) * scaleY };
  };

  const start = (e) => { e.preventDefault(); drawingRef.current = true; lastRef.current = getPos(e); };
  const move = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(lastRef.current.x, lastRef.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    lastRef.current = pos;
    hasDrawnRef.current = true;
  };
  const end = () => { drawingRef.current = false; };

  const clear = () => {
    const ctx = canvasRef.current.getContext('2d');
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    hasDrawnRef.current = false;
  };

  const save = () => {
    if (!hasDrawnRef.current) return toast.error('Bitte unterschreiben Sie zuerst');
    const data = canvasRef.current.toDataURL('image/png');
    onSave(data);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{side === 'admin' ? 'Unterschrift Auftragnehmer' : 'Unterschrift Auftraggeber'}</DialogTitle>
          <DialogDescription>Unterschreiben Sie mit Maus, Finger oder Stift im Feld unten.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="border-2 border-dashed border-gray-300 rounded-lg overflow-hidden bg-white">
            <canvas
              ref={canvasRef}
              width={800}
              height={300}
              className="w-full touch-none"
              style={{ display: 'block', height: '220px' }}
              onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
              onTouchStart={start} onTouchMove={move} onTouchEnd={end}
              data-testid="signature-canvas"
            />
          </div>
          <p className="text-xs text-gray-500">Bitte mit Maus, Finger oder Stift unterschreiben.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={clear}><Eraser className="w-4 h-4 mr-2" /> Löschen</Button>
          <Button onClick={save} className="bg-[#9B1B30] hover:bg-[#9B1B30]/90 text-white" data-testid="signature-save-btn">
            <CheckCircle2 className="w-4 h-4 mr-2" /> Speichern
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminContractDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [contract, setContract] = useState(null);
  const [signSide, setSignSide] = useState(null);

  const load = async () => {
    const res = await fetch(`${API}/api/admin/contracts/${id}`, { credentials: 'include' });
    if (res.ok) setContract(await res.json());
    else toast.error('Vertrag nicht gefunden');
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  const handleSign = async (dataUrl) => {
    const res = await fetch(`${API}/api/admin/contracts/${id}/sign`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrfToken() || '' },
      body: JSON.stringify({ side: signSide, signature_data_url: dataUrl }),
    });
    if (res.ok) { toast.success('Unterschrift gespeichert'); setSignSide(null); load(); }
    else toast.error('Speichern fehlgeschlagen');
  };

  const handleStatusChange = async (status) => {
    const res = await fetch(`${API}/api/admin/contracts/${id}`, {
      method: 'PUT', credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrfToken() || '' },
      body: JSON.stringify({ status }),
    });
    if (res.ok) { toast.success('Status aktualisiert'); load(); }
  };

  const handleDownload = async () => {
    const res = await fetch(`${API}/api/admin/contracts/${id}/pdf`, { credentials: 'include' });
    if (!res.ok) return toast.error('PDF nicht verfügbar');
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `Vertrag_${contract.contract_number}.pdf`; a.click();
    URL.revokeObjectURL(url);
  };

  const handleEmail = async () => {
    if (!window.confirm(`Vertrag per E-Mail an ${contract.customer?.email || '(keine E-Mail)'} senden?`)) return;
    const res = await fetch(`${API}/api/admin/contracts/${id}/send-email`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrfToken() || '' },
      body: JSON.stringify({}),
    });
    if (res.ok) { toast.success('Vertrag versendet'); load(); }
    else { const e = await res.json().catch(() => ({})); toast.error(e.detail || 'Senden fehlgeschlagen'); }
  };

  const handleDuplicate = async () => {
    const res = await fetch(`${API}/api/admin/contracts/${id}/duplicate`, {
      method: 'POST', credentials: 'include', headers: { 'X-CSRF-Token': getCsrfToken() || '' },
    });
    if (res.ok) {
      const d = await res.json();
      toast.success('Vertrag dupliziert');
      navigate(`/admin/vertraege/${d.id}`);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Vertrag wirklich löschen?')) return;
    const res = await fetch(`${API}/api/admin/contracts/${id}`, {
      method: 'DELETE', credentials: 'include', headers: { 'X-CSRF-Token': getCsrfToken() || '' },
    });
    if (res.ok) { toast.success('Vertrag gelöscht'); navigate('/admin/vertraege'); }
  };

  if (!contract) return <div className="p-8 text-gray-500">Lade Vertrag…</div>;

  const status = STATUS_CONFIG[contract.status] || STATUS_CONFIG.draft;
  const cust = contract.customer || {};
  const job = contract.job || {};
  const services = contract.services || {};
  const totals = contract.totals || {};
  const SERVICE_LABELS = {
    transport: 'Transport', loading: 'Be- und Entladung', furniture_assembly: 'Möbelmontage',
    kitchen_assembly: 'Küchenmontage', packing_service: 'Verpackungsservice', decluttering: 'Entrümpelung', storage: 'Einlagerung',
  };
  const activeServices = Object.entries(SERVICE_LABELS).filter(([k]) => services[k]).map(([_, v]) => v);

  return (
    <div className="space-y-6" data-testid="contract-detail">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={() => navigate('/admin/vertraege')}><ArrowLeft className="w-4 h-4 mr-1" /> Zurück</Button>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-barlow font-bold text-[#1A1A1A]">{contract.contract_number}</h1>
              <Badge variant="outline" className={status.className}>{status.label}</Badge>
            </div>
            <p className="text-sm text-gray-500">Erstellt am {new Date(contract.created_at).toLocaleDateString('de-DE')}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={handleDownload} data-testid="contract-download-detail-btn"><Download className="w-4 h-4 mr-1" /> PDF</Button>
          <Button variant="outline" size="sm" onClick={handleEmail}><Mail className="w-4 h-4 mr-1" /> E-Mail</Button>
          <Button variant="outline" size="sm" onClick={handleDuplicate}><Copy className="w-4 h-4 mr-1" /> Duplizieren</Button>
          <Link to={`/admin/vertraege/${id}/bearbeiten`}>
            <Button variant="outline" size="sm"><FilePenLine className="w-4 h-4 mr-1" /> Bearbeiten</Button>
          </Link>
          <Button variant="outline" size="sm" onClick={handleDelete} className="hover:bg-rose-50 hover:text-rose-700"><Trash2 className="w-4 h-4" /></Button>
        </div>
      </div>

      {/* Status Quick-Actions */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-gray-500 mr-2">Status setzen:</span>
            {Object.entries(STATUS_CONFIG).map(([k, v]) => (
              <button
                key={k}
                onClick={() => handleStatusChange(k)}
                disabled={contract.status === k}
                className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors disabled:opacity-100 ${contract.status === k ? v.className + ' ring-2 ring-offset-1 ring-[#9B1B30]/30' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'}`}
                data-testid={`status-set-${k}`}
              >{v.label}</button>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="grid lg:grid-cols-3 gap-5">
        {/* Customer */}
        <Card className="lg:col-span-2">
          <CardContent className="p-6 space-y-3">
            <h2 className="font-bold text-lg mb-3">Auftraggeber</h2>
            <div className="grid sm:grid-cols-2 gap-3 text-sm">
              <div><p className="text-gray-500">Name</p><p className="font-medium">{cust.first_name} {cust.last_name}</p></div>
              <div><p className="text-gray-500">Telefon</p><p className="font-medium">{cust.phone || '—'}</p></div>
              <div><p className="text-gray-500">E-Mail</p><p className="font-medium">{cust.email || '—'}</p></div>
              <div><p className="text-gray-500">Alte Adresse</p><p className="font-medium">{cust.old_address || '—'}</p></div>
              <div className="sm:col-span-2"><p className="text-gray-500">Neue Adresse</p><p className="font-medium">{cust.new_address || '—'}</p></div>
            </div>
            <hr className="my-4" />
            <h2 className="font-bold text-lg mb-3">Auftragsdaten</h2>
            <div className="grid sm:grid-cols-2 gap-3 text-sm">
              <div><p className="text-gray-500">Datum</p><p className="font-medium">{job.move_date} {job.move_time && `· ${job.move_time}`}</p></div>
              <div><p className="text-gray-500">Mitarbeiter</p><p className="font-medium">{job.staff_count}</p></div>
              <div><p className="text-gray-500">Fahrzeug</p><p className="font-medium">{job.vehicle_size || '—'}</p></div>
              <div><p className="text-gray-500">Stockwerke</p><p className="font-medium">{job.floors || '—'} {job.elevator ? '· Aufzug' : '· kein Aufzug'}</p></div>
              <div><p className="text-gray-500">Laufweg</p><p className="font-medium">{job.distance_walk || '—'}</p></div>
              <div><p className="text-gray-500">Halteverbotszone</p><p className="font-medium">{job.no_parking_zone ? 'Ja' : 'Nein'}</p></div>
            </div>
            <hr className="my-4" />
            <h2 className="font-bold text-lg mb-3">Leistungen</h2>
            <div className="flex flex-wrap gap-2">
              {activeServices.length ? activeServices.map(s => <Badge key={s} className="bg-[#9B1B30] hover:bg-[#9B1B30]/90 text-white">{s}</Badge>) : <span className="text-gray-500 text-sm">Keine Leistungen ausgewählt</span>}
            </div>
          </CardContent>
        </Card>

        {/* Pricing + Signatures */}
        <div className="space-y-5">
          <Card>
            <CardContent className="p-6">
              <h2 className="font-bold text-lg mb-3">Preise</h2>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-gray-500">Grundpreis</span><span className="font-medium">{(totals.base_total || 0).toFixed(2)} €</span></div>
                {(contract.pricing?.additional_costs || 0) > 0 && (
                  <div className="flex justify-between"><span className="text-gray-500">Zusatzkosten</span><span className="font-medium">{Number(contract.pricing.additional_costs).toFixed(2)} €</span></div>
                )}
                <div className="flex justify-between border-t-2 border-[#9B1B30] pt-2 mt-2"><span className="font-bold">Brutto</span><span className="font-bold text-[#9B1B30]">{(totals.total_gross || 0).toFixed(2)} €</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Anzahlung</span><span>{(totals.deposit || 0).toFixed(2)} €</span></div>
                <div className="flex justify-between"><span className="font-medium">Restzahlung</span><span className="font-bold">{(totals.remaining || 0).toFixed(2)} €</span></div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6 space-y-4">
              <h2 className="font-bold text-lg mb-1">Unterschriften</h2>
              <div className="space-y-3">
                <div className="border rounded-lg p-3 bg-gray-50">
                  <p className="text-xs text-gray-500 mb-1">Auftraggeber</p>
                  {contract.customer_signature ? (
                    <div>
                      <img src={contract.customer_signature} alt="Unterschrift Kunde" className="h-16 bg-white border rounded p-2" />
                      <p className="text-xs text-emerald-700 mt-1">✓ Unterschrieben am {new Date(contract.customer_signed_at).toLocaleDateString('de-DE')}</p>
                    </div>
                  ) : (
                    <Button size="sm" onClick={() => setSignSide('customer')} className="bg-[#9B1B30] hover:bg-[#9B1B30]/90 text-white" data-testid="sign-customer-btn">
                      <Pen className="w-3 h-3 mr-1" /> Jetzt unterschreiben
                    </Button>
                  )}
                </div>
                <div className="border rounded-lg p-3 bg-gray-50">
                  <p className="text-xs text-gray-500 mb-1">Auftragnehmer</p>
                  {contract.admin_signature ? (
                    <div>
                      <img src={contract.admin_signature} alt="Unterschrift Admin" className="h-16 bg-white border rounded p-2" />
                      <p className="text-xs text-emerald-700 mt-1">✓ Unterschrieben am {new Date(contract.admin_signed_at).toLocaleDateString('de-DE')}</p>
                    </div>
                  ) : (
                    <Button size="sm" onClick={() => setSignSide('admin')} className="bg-[#2E2A88] hover:bg-[#2E2A88]/90 text-white" data-testid="sign-admin-btn">
                      <Pen className="w-3 h-3 mr-1" /> Jetzt unterschreiben
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {signSide && <SignaturePad side={signSide} onSave={handleSign} onClose={() => setSignSide(null)} />}
    </div>
  );
}
